import React, { useRef, useState } from 'react';
import { SortByOption } from '@kimuno/api-sdk/lib/types/requests/Common';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Radio,
  ListItemText,
  alpha,
  Box,
} from '@mui/material';
import { colors } from '../../../assets/styles/themes/variables';
import { BasicFilterProps } from '../../../types/search';
import { spaces } from '../../../assets/styles/themes/tokens';
import { Option, useSortFilterOptions } from '../SortByFilterOptions';
import getMuiIcon from '../../../utils/getMuiIcon';
import classNames from 'classnames';
import useScreenSize from '../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';

const SortByFilterNewUI: React.FC<BasicFilterProps<SortByOption>> = (
  { currentValue, updateFilter }
) => {
  const { t } =  useTranslation();
  const [isOpen, setOpen] = useState(false);
  const { isXsScreen } = useScreenSize();
  const formControlRef = useRef<HTMLDivElement>(null);

  const handleChange = (event: SelectChangeEvent) => {
    updateFilter(event.target.value as SortByOption);
  };

  const sortFilterOptions: Option[] = useSortFilterOptions();

  return (
    <FormControl
      data-testid='sort-by-new-ui-form-control'
      ref={formControlRef}
      sx={{
        height: spaces.xxl,
        '& .MuiButtonBase-root.MuiMenuItem-root:hover': {
          backgroundColor: 'transparent!important',
        },
        '& .MuiInputBase-root': {
          width: isXsScreen ? '138px' : '220px',
          padding: `0px ${spaces.sm}`,
        },
        '& .MuiInputBase-input': {
          padding: '0px',
        },
        '& .MuiMenuItem-root': {
          padding: 0,
        },
      }}
    >
      <Select
        value={currentValue || 'relevance'}
        inputProps={{
          'aria-label': t('Sort by'),
        }}
        IconComponent={({ className, selectIcon, ...props }) => <Box
          className={classNames(className, selectIcon)}
          component={getMuiIcon('arrowDropdownFilled')}
          {...props}
          sx={{
            top: 'unset !important',
            color: alpha(colors.violet800, 0.48) + ' !important',
          }}
        /> }
        renderValue={(selected) => {
          const selectedOption = sortFilterOptions.find((op) => op.value === selected);
          return (
            <Box
              component='span'
              sx={{
                maxWidth: isXsScreen ? '100px' : '100%',
                overflow: isXsScreen ? 'hidden' : 'visible',
                fontSize: spaces.md,
                letterSpacing: '0.15px',
                fontWeight: '400',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {selectedOption?.name}
            </Box>
          );
        }}
        onChange={handleChange}
        onOpen={() => {
          setOpen(true);
          formControlRef.current?.querySelector('.MuiInputBase-root')?.classList.add('Mui-focused');
        }}
        onClose={() => {
          setOpen(false);
          formControlRef.current?.querySelector('.MuiInputBase-root')?.classList.remove('Mui-focused');
        }}
        open={isOpen}
        MenuProps={{
          role: 'listbox',
          PaperProps: {
            sx: {
              width: '220px',
              marginTop: spaces.xs,
              borderRadius: spaces.md,
              ...(isXsScreen && {
                right:  spaces.xs,
                left: 'unset!important',
              }),
            },
          },
        }}
        sx={{
          height: spaces.xxl,
          borderRadius: spaces.sm,
        }}
      >
        {sortFilterOptions.map((option) => (
          <MenuItem
            data-testid='sort-by-new-ui-menu-item'
            key={option.value}
            value={option.value}
            sx={{
              padding: '0px',
              margin: `0px ${spaces.xs}`,
              height: '50px',
              gap: '0px',
              '&:hover': {
                backgroundColor: `${alpha(colors.blue500, 0.08)}`,
                borderRadius: spaces.md,
              },
              backgroundColor: currentValue === option.value ?
                `${alpha(colors.blue500, 0.08)}!important` :
                'transparent',
              borderRadius: spaces.md,
            }}
          >
            <Radio
              checked={currentValue === option.value}
              sx={{
                visibility: isOpen ? 'visible' : 'hidden',
                gap: '0px!important',
              }}
            />
            <ListItemText primary={option.name} sx={{
              '& .MuiTypography-root': {
                letterSpacing: '0.17px',
                fontWeight: '400',
                fontSize: '14px',
                lineHeight: spaces.lg,
              },
            }}/>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SortByFilterNewUI;
